import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getConsumos } from "../../redux/actions";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
    FaFileExcel,
    FaFilePdf,
    FaFilter,
    FaCalendarAlt,
    FaTools,
    FaUndo,
    FaChartLine,
    FaTicketAlt,
    FaUserTie,
    FaLayerGroup
} from "react-icons/fa";
import styles from "./Consumo.module.css";

const ConsumoTable = () => {
    const dispatch = useDispatch();
    const consumos = useSelector((state) => state.consumo);

    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");
    const [componenteSeleccionado, setComponenteSeleccionado] = useState("");

    const componentesDisponibles = [
        "discosata", "memoriaram", "cargador", "procesador", "tarjeredlam",
        "pantalla", "bateinterna", "mouse", "diadema", "multiusb", "padmouse",
        "disssd", "bateria", "tarjemadre", "tarjevideo", "tarjeredwlan",
        "fuentepod", "teclado", "combinalamb", "adaptarj45", "fundaporta"
    ];

    useEffect(() => {
        dispatch(getConsumos());
    }, [dispatch]);

    const filteredData = consumos.filter((res) => {
        const fechaRegistro = new Date(res.Ticketregistro.fecha).toISOString().split("T")[0];

        const cumpleFecha =
            (!fechaDesde || fechaRegistro >= fechaDesde) &&
            (!fechaHasta || fechaRegistro <= fechaHasta);

        const cumpleComponente =
            !componenteSeleccionado ||
            (res[componenteSeleccionado] && res[componenteSeleccionado] > 0);

        return cumpleFecha && cumpleComponente;
    });

    const resetFilters = () => {
        setFechaDesde("");
        setFechaHasta("");
        setComponenteSeleccionado("");
    };

    const exportToExcel = () => {
        const dataToExport = filteredData.flatMap(res => {
            return Object.entries(res)
                .filter(([key, value]) => componentesDisponibles.includes(key) && value > 0)
                .map(([componente, cantidad]) => ({
                    Ticket: res.TicketId,
                    Fecha: new Date(res.Ticketregistro.fecha).toISOString().split("T")[0],
                    Técnico: res.Ticketregistro.UsuarioTecnico,
                    Usuario: res.UsuarioAsignado || "No asignado",
                    "Componente Usado": componente,
                    Cantidad: cantidad
                }));
        });

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Consumos");
        XLSX.writeFile(wb, "Consumos_Incolab.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Reporte de Consumos de Repuestos", 14, 15);

        const dataToExport = filteredData.flatMap(res => {
            return Object.entries(res)
                .filter(([key, value]) => componentesDisponibles.includes(key) && value > 0)
                .map(([componente, cantidad]) => [
                    res.TicketId,
                    new Date(res.Ticketregistro.fecha).toISOString().split("T")[0],
                    res.Ticketregistro.UsuarioTecnico,
                    res.UsuarioAsignado || "No asignado",
                    componente,
                    cantidad
                ]);
        });

        doc.autoTable({
            head: [["Ticket", "Fecha", "Técnico", "Usuario", "Componente", "Cantidad"]],
            body: dataToExport,
            startY: 25,
            theme: 'grid',
            headStyles: { fillColor: [59, 130, 246] }
        });

        doc.save("Consumos_Incolab.pdf");
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h2 className={styles.title}>
                    <FaChartLine /> Listado de Consumos
                </h2>
                <p className={styles.subtitle}>Control y trazabilidad de repuestos y suministros utilizados por el departamento técnico.</p>
            </header>

            <div className={styles.filterCard}>
                <div className={styles.filterGroup}>
                    <label><FaCalendarAlt /> Fecha Inicial</label>
                    <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
                </div>

                <div className={styles.filterGroup}>
                    <label><FaCalendarAlt /> Fecha Final</label>
                    <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
                </div>

                <div className={styles.filterGroup}>
                    <label><FaTools /> Filtrar Repuesto</label>
                    <select value={componenteSeleccionado} onChange={(e) => setComponenteSeleccionado(e.target.value)}>
                        <option value="">Todos los componentes</option>
                        {componentesDisponibles.map((comp) => (
                            <option key={comp} value={comp}>{comp}</option>
                        ))}
                    </select>
                </div>

                <button className={styles.resetButton} onClick={resetFilters}>
                    <FaUndo /> Limpiar
                </button>
            </div>

            <div className={styles.exportActions}>
                <button className={styles.excelButton} onClick={exportToExcel}>
                    <FaFileExcel /> Exportar Excel
                </button>
                <button className={styles.pdfButton} onClick={exportToPDF}>
                    <FaFilePdf /> Exportar PDF
                </button>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th><FaTicketAlt style={{ marginRight: '8px' }} /> Ticket</th>
                            <th><FaCalendarAlt style={{ marginRight: '8px' }} /> Fecha</th>
                            <th><FaUserTie style={{ marginRight: '8px' }} /> Técnico</th>
                            <th><FaUserTie style={{ marginRight: '8px' }} /> Usuario</th>
                            <th><FaTools style={{ marginRight: '8px' }} /> Repuesto</th>
                            <th><FaLayerGroup style={{ marginRight: '8px' }} /> Cant.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.map((res, index) => {
                                const componentesUsados = Object.entries(res)
                                    .filter(([key, value]) => componentesDisponibles.includes(key) && value > 0);

                                return componentesUsados.length > 0 ? (
                                    componentesUsados.map(([componente, cantidad], compIndex) => (
                                        <tr key={`${index}-${compIndex}`}>
                                            {compIndex === 0 && (
                                                <>
                                                    <td rowSpan={componentesUsados.length}>
                                                        <span className={styles.ticketId}>{res.TicketId}</span>
                                                    </td>
                                                    <td rowSpan={componentesUsados.length}>
                                                        {new Date(res.Ticketregistro.fecha).toISOString().split("T")[0]}
                                                    </td>
                                                    <td rowSpan={componentesUsados.length}>{res.Ticketregistro.UsuarioTecnico}</td>
                                                    <td rowSpan={componentesUsados.length}>{res.UsuarioAsignado || "No asignado"}</td>
                                                </>
                                            )}
                                            <td style={{ textTransform: 'capitalize', fontWeight: '500' }}>{componente}</td>
                                            <td>
                                                <span className={styles.cantidadBadge}>{cantidad}</span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr key={index}>
                                        <td><span className={styles.ticketId}>{res.TicketId}</span></td>
                                        <td>{new Date(res.Ticketregistro.fecha).toISOString().split("T")[0]}</td>
                                        <td>{res.Ticketregistro.UsuarioTecnico}</td>
                                        <td>{res.UsuarioAsignado || "No asignado"}</td>
                                        <td colSpan="2" className={styles.noData}>No se registraron repuestos</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" className={styles.noData}>
                                    No se encontraron registros de consumo para los filtros aplicados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ConsumoTable;
